import { useEffect, useMemo, useState } from "react";
import type { Need, NeedVisibility, Service } from "@hub/shared";
import { Button, Field, Input, Modal, Select, Textarea, toast } from "@/ui";
import { CATEGORIES, DEFAULT_CATEGORY, TIMELINE_SUGGESTIONS, VISIBILITY_OPTIONS, inferCategory } from "../lib/categories";
import { useSaveNeed, useSaveService } from "../lib/hooks";
import { useEngineQuery } from "../_shims";
import { KeywordChips } from "./KeywordChips";
import s from "../network.module.css";

export type ItemKind = "need" | "service";

export interface ItemFormValues {
  title: string;
  description: string;
  category: string;
  timeline: string;
  location: string;
  visibility: NeedVisibility;
  keywords: string[];
}

export function emptyValues(kind: ItemKind): ItemFormValues {
  return { title: "", description: "", category: kind === "service" ? "Uncategorized" : DEFAULT_CATEGORY, timeline: "", location: "", visibility: "connections", keywords: [] };
}

export function valuesFromNeed(n: Need): ItemFormValues {
  return { title: n.title, description: n.description, category: n.category, timeline: n.timeline ?? "", location: n.location ?? "", visibility: n.visibility, keywords: n.keywords };
}

export function valuesFromService(sv: Service): ItemFormValues {
  return { title: sv.title, description: sv.shortDescription, category: sv.category, timeline: "", location: sv.location ?? "", visibility: sv.visibility, keywords: sv.keywords };
}

export function validateItem(v: ItemFormValues): Partial<Record<keyof ItemFormValues, string>> {
  const errors: Partial<Record<keyof ItemFormValues, string>> = {};
  if (v.title.trim().length < 3) errors.title = "Give it a short title (at least 3 characters).";
  if (v.title.trim().length > 120) errors.title = "Keep the title under 120 characters.";
  if (v.description.trim().length > 2000) errors.description = "Keep the description under 2000 characters.";
  if (!v.category) errors.category = "Pick a category.";
  return errors;
}

export interface ItemFormProps {
  kind: ItemKind;
  contactId: string;
  /** Existing item to edit; omit to create. */
  need?: Need | null;
  service?: Service | null;
  open: boolean;
  onClose: () => void;
  onSaved?: (item: Need | Service) => void;
  /** Shown above the form, e.g. the person's name. */
  forLabel?: string;
}

/** Add or edit a need or a service. One form, two save paths. */
export function ItemForm({ kind, contactId, need, service, open, onClose, onSaved, forLabel }: ItemFormProps) {
  const [values, setValues] = useState<ItemFormValues>(() => emptyValues(kind));
  const [touched, setTouched] = useState(false);
  const saveNeed = useSaveNeed();
  const saveService = useSaveService();
  const saving = saveNeed.isPending || saveService.isPending;

  useEffect(() => {
    if (!open) return;
    setTouched(false);
    if (kind === "need" && need) setValues(valuesFromNeed(need));
    else if (kind === "service" && service) setValues(valuesFromService(service));
    else setValues(emptyValues(kind));
  }, [open, kind, need, service]);

  const errors = useMemo(() => validateItem(values), [values]);
  const set = <K extends keyof ItemFormValues>(k: K, v: ItemFormValues[K]) => setValues((prev) => ({ ...prev, [k]: v }));
  const sourceText = values.title;

  const submit = async () => {
    setTouched(true);
    if (Object.keys(errors).length > 0) return;
    try {
      if (kind === "need") {
        const saved = await saveNeed.mutateAsync({
          ...(need ? { id: need.id } : {}),
          contactId,
          title: values.title.trim(),
          description: values.description.trim(),
          category: values.category,
          timeline: values.timeline.trim() || null,
          location: values.location.trim() || null,
          visibility: values.visibility,
          keywords: values.keywords,
          keywordsGeneratedByAi: false,
        });
        onSaved?.(saved);
      } else {
        const saved = await saveService.mutateAsync({
          ...(service ? { id: service.id } : {}),
          contactId,
          title: values.title.trim(),
          shortDescription: values.description.trim(),
          category: values.category,
          location: values.location.trim() || null,
          visibility: values.visibility,
          keywords: values.keywords,
          keywordsGeneratedByAi: false,
          isActive: service?.isActive ?? true,
        });
        onSaved?.(saved);
      }
      toast(kind === "need" ? "Need saved." : "Offer saved.", "success");
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Could not save.", "danger");
    }
  };

  const editing = Boolean(kind === "need" ? need : service);
  // On your own card the form speaks to you; on anyone else's it names them.
  const self = useEngineQuery("getSelfContact", undefined);
  const mine = forLabel === "me" || (!!contactId && self.data?.contact.id === contactId);
  const title = mine ? `${editing ? "Edit your" : "Add your"} ${kind === "need" ? "need" : "offer"}` : `${editing ? "Edit" : "Add"} ${kind === "need" ? "a need" : "an offer"}${forLabel ? ` for ${forLabel}` : ""}`;
  const categories = kind === "service" ? ["Uncategorized", ...CATEGORIES] : [...CATEGORIES];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={saving} data-testid="item-save">
            {editing ? "Save changes" : kind === "need" ? "Add need" : "Add offer"}
          </Button>
        </>
      }
    >
      <form
        className={s.formGrid}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") void submit();
        }}
        data-testid="item-form"
      >
        <div className="full">
          <Field label={kind === "need" ? (mine ? "What do you need?" : "What do they need?") : mine ? "What do you offer?" : "What do they offer?"} error={touched ? errors.title : null}>
            <Input
              value={values.title}
              onChange={(e) => {
                set("title", e.target.value);
                if (!editing && (values.category === DEFAULT_CATEGORY || values.category === "Uncategorized")) {
                  const inferred = inferCategory(`${e.target.value} ${values.description}`);
                  if (inferred !== DEFAULT_CATEGORY) set("category", inferred);
                }
              }}
              placeholder={kind === "need" ? "An accountant who knows startups" : "Fractional CFO for early stage companies"}
              autoFocus
              aria-label="Title"
            />
          </Field>
        </div>
        <div className="full">
          <Field label="Details" hint="Context helps matching. Keep names and private details out." error={touched ? errors.description : null}>
            <Textarea value={values.description} onChange={(e) => set("description", e.target.value)} rows={3} aria-label="Description" />
          </Field>
        </div>
        <Field label="Category" error={touched ? errors.category : null}>
          <Select value={values.category} onChange={(e) => set("category", e.target.value)} aria-label="Category">
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </Field>
        {kind === "need" ? (
          <Field label="Timeline" hint="Free text, e.g. this quarter.">
            <Input value={values.timeline} onChange={(e) => set("timeline", e.target.value)} list="hub-timeline-suggestions" aria-label="Timeline" />
            <datalist id="hub-timeline-suggestions">
              {TIMELINE_SUGGESTIONS.map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
          </Field>
        ) : (
          <Field label="Location" hint="City or region where this is offered.">
            <Input value={values.location} onChange={(e) => set("location", e.target.value)} aria-label="Location" />
          </Field>
        )}
        {kind === "need" && (
          <Field label="Location" hint="Where it matters, if anywhere.">
            <Input value={values.location} onChange={(e) => set("location", e.target.value)} aria-label="Location" />
          </Field>
        )}
        <Field label="Who can see it" hint={VISIBILITY_OPTIONS.find((o) => o.value === values.visibility)?.hint}>
          <Select value={values.visibility} onChange={(e) => set("visibility", e.target.value as NeedVisibility)} aria-label="Visibility">
            {VISIBILITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>
        <div className="full">
          <Field label="Keywords">
            <KeywordChips value={values.keywords} onChange={(k) => set("keywords", k)} sourceText={sourceText} sourceDetails={values.description} />
          </Field>
        </div>
        <button type="submit" className={s.srOnly} tabIndex={-1} aria-hidden>
          Save
        </button>
      </form>
    </Modal>
  );
}
